import React, { useState } from 'react';
import './DocumentationPage.css';

interface DocumentationPageProps {
  onBackToHome: () => void;
  onGetStarted: () => void;
}

const DocumentationPage: React.FC<DocumentationPageProps> = ({ onBackToHome, onGetStarted }) => {
  const [activeSection, setActiveSection] = useState<string>('getting-started');

  const sections = [
    { id: 'getting-started', title: 'Getting Started', icon: '🚀' },
    { id: 'widget-configuration', title: 'Widget Configuration', icon: '⚙️' },
    { id: 'audience-targeting', title: 'Audience Targeting', icon: '🎯' },
    { id: 'analysis-modes', title: 'Analysis Modes', icon: '🔍' },
    { id: 'ai-platforms', title: 'AI Platform Integration', icon: '🤖' },
    { id: 'embedding', title: 'Embedding Widgets', icon: '📦' },
    { id: 'customization', title: 'Customization Options', icon: '🎨' },
    { id: 'analytics', title: 'Analytics & Tracking', icon: '📊' },
    { id: 'api-reference', title: 'API Reference', icon: '📚' },
    { id: 'troubleshooting', title: 'Troubleshooting', icon: '🔧' }
  ];

  return (
    <div className="docs-page">
      {/* Header */}
      <header className="docs-page__header">
        <div className="docs-page__container">
          <button className="docs-page__back-button" onClick={onBackToHome}>
            ← Back to Home
          </button>
          <div className="docs-page__header-content">
            <h1 className="docs-page__title">Documentation</h1>
            <p className="docs-page__subtitle">
              Complete guide to building and deploying widgets that advance how data is understood
            </p>
          </div>
        </div>
      </header>

      <div className="docs-page__layout">
        {/* Sidebar Navigation */}
        <nav className="docs-page__sidebar">
          <div className="docs-page__nav">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`docs-page__nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="docs-page__nav-icon">{section.icon}</span>
                {section.title}
              </button>
            ))}
          </div>
        </nav>

        {/* Main Content */}
        <main className="docs-page__content">
          {activeSection === 'getting-started' && (
            <div className="docs-section">
              <h2>🚀 Getting Started</h2>
              <p>Welcome to Faathum! This guide will help you create your first widget that advances how data is understood in minutes.</p>
              
              <h3>What is Faathum?</h3>
              <p>Faathum is a platform that enables organizations to create intelligent data exploration widgets. These widgets connect users directly to leading AI platforms with contextual, audience-specific prompts.</p>
              
              <h3>Quick Start</h3>
              <div className="docs-steps">
                <div className="docs-step">
                  <div className="docs-step-number">1</div>
                  <div className="docs-step-content">
                    <h4>Configure Your Dataset</h4>
                    <p>Add your data source information, description, and metadata using our intuitive builder interface.</p>
                  </div>
                </div>
                <div className="docs-step">
                  <div className="docs-step-number">2</div>
                  <div className="docs-step-content">
                    <h4>Choose Your Audience</h4>
                    <p>Select target audience (General, Journalist, Researcher, Policy) to customize AI prompts.</p>
                  </div>
                </div>
                <div className="docs-step">
                  <div className="docs-step-number">3</div>
                  <div className="docs-step-content">
                    <h4>Enable Analysis Modes</h4>
                    <p>Choose which analysis approaches to offer: General, Trends, Anomalies, Policy Analysis.</p>
                  </div>
                </div>
                <div className="docs-step">
                  <div className="docs-step-number">4</div>
                  <div className="docs-step-content">
                    <h4>Deploy Your Widget</h4>
                    <p>Generate embed code and add the widget to your website with a simple HTML snippet.</p>
                  </div>
                </div>
              </div>
              
              <div className="docs-cta">
                <button className="docs-cta-button" onClick={onGetStarted}>
                  Start Building Your Widget
                </button>
              </div>
            </div>
          )}

          {activeSection === 'widget-configuration' && (
            <div className="docs-section">
              <h2>⚙️ Widget Configuration</h2>
              <p>Learn how to configure your widget with the right metadata and settings for optimal user experience.</p>
              
              <h3>Required Fields</h3>
              <div className="docs-field-list">
                <div className="docs-field">
                  <strong>Title:</strong> A clear, descriptive name for your dataset or visualization
                </div>
                <div className="docs-field">
                  <strong>Description:</strong> Detailed explanation of what the data covers and its significance
                </div>
                <div className="docs-field">
                  <strong>Source Name:</strong> The organization or entity that provides the data
                </div>
                <div className="docs-field">
                  <strong>Source URL:</strong> Direct link to the data source or interactive visualization
                </div>
              </div>

              <h3>Optional Fields</h3>
              <div className="docs-field-list">
                <div className="docs-field">
                  <strong>Format:</strong> Type of data presentation (Dashboard, Chart, Map, etc.)
                </div>
                <div className="docs-field">
                  <strong>Last Updated:</strong> When the data was last refreshed or updated
                </div>
                <div className="docs-field">
                  <strong>Context:</strong> Additional background information to enhance AI prompts
                </div>
              </div>

              <h3>Best Practices</h3>
              <ul>
                <li>Use clear, jargon-free titles that immediately convey the data's purpose</li>
                <li>Write descriptions that explain both what the data shows and why it matters</li>
                <li>Include update frequency information to set user expectations</li>
                <li>Add context that helps AI platforms provide more relevant analysis</li>
              </ul>
            </div>
          )}

          {activeSection === 'audience-targeting' && (
            <div className="docs-section">
              <h2>🎯 Audience Targeting</h2>
              <p>Customize AI prompts based on your target audience to ensure relevant, actionable insights.</p>
              
              <h3>Available Audiences</h3>
              <div className="docs-audience-grid">
                <div className="docs-audience-card">
                  <h4>👥 General</h4>
                  <p>Broad audience with varied backgrounds. Prompts focus on accessible explanations and practical insights.</p>
                </div>
                <div className="docs-audience-card">
                  <h4>📰 Journalist</h4>
                  <p>Media professionals seeking story angles, trends, and newsworthy insights from data.</p>
                </div>
                <div className="docs-audience-card">
                  <h4>🔬 Researcher</h4>
                  <p>Academic and professional researchers needing detailed analysis and methodological insights.</p>
                </div>
                <div className="docs-audience-card">
                  <h4>🏛️ Policy</h4>
                  <p>Policymakers and analysts focused on regulatory implications and policy recommendations.</p>
                </div>
              </div>

              <h3>How Audience Targeting Works</h3>
              <p>When users interact with your widget, the AI prompts are automatically customized based on the selected audience:</p>
              <ul>
                <li><strong>Language and Tone:</strong> Adjusted for the audience's expertise level</li>
                <li><strong>Focus Areas:</strong> Emphasizes aspects most relevant to each audience</li>
                <li><strong>Output Format:</strong> Structured for how each audience typically consumes information</li>
                <li><strong>Context Awareness:</strong> Includes domain-specific considerations and implications</li>
              </ul>
            </div>
          )}

          {activeSection === 'analysis-modes' && (
            <div className="docs-section">
              <h2>🔍 Analysis Modes</h2>
              <p>Offer different analytical approaches to help users explore data from multiple perspectives.</p>
              
              <h3>Available Analysis Modes</h3>
              <div className="docs-mode-grid">
                <div className="docs-mode-card">
                  <h4>📊 General Analysis</h4>
                  <p>Comprehensive overview covering key insights, patterns, and takeaways from the data.</p>
                  <strong>Best for:</strong> Initial exploration and broad understanding
                </div>
                <div className="docs-mode-card">
                  <h4>📈 Trend Analysis</h4>
                  <p>Focus on temporal patterns, changes over time, and directional movements in the data.</p>
                  <strong>Best for:</strong> Time-series data and longitudinal studies
                </div>
                <div className="docs-mode-card">
                  <h4>🔍 Anomaly Detection</h4>
                  <p>Identify outliers, unusual patterns, and data points that deviate from expected norms.</p>
                  <strong>Best for:</strong> Quality control and identifying exceptional cases
                </div>
                <div className="docs-mode-card">
                  <h4>🏛️ Policy Analysis</h4>
                  <p>Examine regulatory implications, policy impacts, and governance considerations.</p>
                  <strong>Best for:</strong> Government data and regulatory compliance
                </div>
              </div>

              <h3>Customizing Analysis Modes</h3>
              <p>You can enable or disable specific analysis modes based on your data type and user needs:</p>
              <ul>
                <li>Select only relevant modes to avoid overwhelming users</li>
                <li>Consider your data characteristics when choosing modes</li>
                <li>All modes can be combined with any audience targeting option</li>
              </ul>
            </div>
          )}

          {activeSection === 'ai-platforms' && (
            <div className="docs-section">
              <h2>🤖 AI Platform Integration</h2>
              <p>Faathum integrates with leading AI platforms to provide users with flexible analysis options.</p>
              
              <h3>Supported Platforms</h3>
              <div className="docs-platform-grid">
                <div className="docs-platform-card">
                  <h4>ChatGPT</h4>
                  <p>OpenAI's conversational AI with strong analytical capabilities and natural language processing.</p>
                  <div className="docs-platform-features">
                    <span>✅ Prompt Prefill</span>
                    <span>✅ Context Awareness</span>
                    <span>✅ Follow-up Questions</span>
                  </div>
                </div>
                <div className="docs-platform-card">
                  <h4>Claude</h4>
                  <p>Anthropic's AI assistant known for detailed analysis and thoughtful reasoning.</p>
                  <div className="docs-platform-features">
                    <span>✅ Prompt Prefill</span>
                    <span>✅ Long-form Analysis</span>
                    <span>✅ Ethical Considerations</span>
                  </div>
                </div>
                <div className="docs-platform-card">
                  <h4>Gemini</h4>
                  <p>Google's multimodal AI with strong data analysis and visualization capabilities.</p>
                  <div className="docs-platform-features">
                    <span>❌ Prompt Prefill</span>
                    <span>✅ Data Integration</span>
                    <span>✅ Visual Analysis</span>
                  </div>
                </div>
                <div className="docs-platform-card">
                  <h4>Perplexity</h4>
                  <p>Research-focused AI that excels at finding and citing relevant sources.</p>
                  <div className="docs-platform-features">
                    <span>✅ Prompt Prefill</span>
                    <span>✅ Source Citations</span>
                    <span>✅ Real-time Data</span>
                  </div>
                </div>
                <div className="docs-platform-card">
                  <h4>Grok</h4>
                  <p>X's AI platform with real-time information access and conversational interface.</p>
                  <div className="docs-platform-features">
                    <span>✅ Prompt Prefill</span>
                    <span>✅ Current Events</span>
                    <span>✅ Social Context</span>
                  </div>
                </div>
              </div>

              <h3>Platform Selection</h3>
              <p>Choose which platforms to enable based on your audience and use case:</p>
              <ul>
                <li><strong>Academic Research:</strong> Claude and Perplexity for detailed analysis</li>
                <li><strong>Business Intelligence:</strong> ChatGPT and Gemini for practical insights</li>
                <li><strong>Journalism:</strong> Perplexity and Grok for current context</li>
                <li><strong>General Use:</strong> All platforms for maximum flexibility</li>
              </ul>
            </div>
          )}

          {activeSection === 'embedding' && (
            <div className="docs-section">
              <h2>📦 Embedding Widgets</h2>
              <p>Learn how to integrate Faathum widgets into your website or application.</p>
              
              <h3>Basic HTML Embed</h3>
              <div className="docs-code-block">
                <pre><code>{`<!-- Add to your HTML -->
<div id="faathum-widget"></div>

<script src="https://cdn.faathum.com/widget.js"></script>
<script>
  FaathumWidget.create({
    target: '#faathum-widget',
    configId: 'your-widget-id'
  });
</script>`}</code></pre>
              </div>

              <h3>React Integration</h3>
              <div className="docs-code-block">
                <pre><code>{`import { ExploreWithAI } from '@faathum/react-widget';

function MyComponent() {
  const config = {
    title: "Economic Data Analysis",
    description: "Federal Reserve economic indicators",
    sourceName: "Federal Reserve",
    sourceUrl: "https://fred.stlouisfed.org/",
    audience: "policy"
  };

  return (
    <ExploreWithAI 
      config={config}
      onAnalytics={(event) => console.log(event)}
    />
  );
}`}</code></pre>
              </div>

              <h3>WordPress Integration</h3>
              <p>Use our WordPress plugin for easy integration:</p>
              <ol>
                <li>Install the Faathum WordPress plugin</li>
                <li>Add your widget configuration in the plugin settings</li>
                <li>Use the shortcode: <code>[faathum-widget id="your-widget-id"]</code></li>
              </ol>

              <h3>Styling and Customization</h3>
              <p>Customize the widget appearance to match your site:</p>
              <div className="docs-code-block">
                <pre><code>{`.faathum-widget {
  --primary-color: #your-brand-color;
  --border-radius: 8px;
  --font-family: 'Your Font', sans-serif;
}`}</code></pre>
              </div>
            </div>
          )}

          {activeSection === 'customization' && (
            <div className="docs-section">
              <h2>🎨 Customization Options</h2>
              <p>Customize the appearance and behavior of your Faathum widgets to match your brand and user experience requirements.</p>
              
              <h3>CSS Custom Properties</h3>
              <p>Override default styling using CSS custom properties (CSS variables):</p>
              <div className="docs-code-block">
                <pre><code>{`.faathum-widget {
  /* Colors */
  --primary-color: #667eea;
  --secondary-color: #764ba2;
  --text-color: #374151;
  --background-color: #ffffff;
  --border-color: #e5e7eb;
  
  /* Typography */
  --font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-size-base: 14px;
  --font-weight-normal: 400;
  --font-weight-bold: 600;
  
  /* Spacing */
  --padding-sm: 8px;
  --padding-md: 16px;
  --padding-lg: 24px;
  --border-radius: 8px;
  
  /* Animation */
  --transition-duration: 0.2s;
  --transition-easing: ease-in-out;
}`}</code></pre>
              </div>

              <h3>Button Styles</h3>
              <p>Customize button appearance and behavior:</p>
              <div className="docs-code-block">
                <pre><code>{`.faathum-widget__button {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: var(--padding-md);
  border-radius: var(--border-radius);
  font-family: var(--font-family);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all var(--transition-duration) var(--transition-easing);
}

.faathum-widget__button:hover {
  background: var(--secondary-color);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}`}</code></pre>
              </div>

              <h3>Layout Options</h3>
              <div className="docs-field-list">
                <div className="docs-field">
                  <strong>Compact Mode:</strong> Minimal button with dropdown on hover
                </div>
                <div className="docs-field">
                  <strong>Expanded Mode:</strong> Full interface with visible options
                </div>
                <div className="docs-field">
                  <strong>Inline Mode:</strong> Horizontal layout for toolbar integration
                </div>
                <div className="docs-field">
                  <strong>Modal Mode:</strong> Opens configuration in overlay dialog
                </div>
              </div>

              <h3>Theme Presets</h3>
              <p>Pre-built themes for common use cases:</p>
              <div className="docs-code-block">
                <pre><code>{`<!-- Light Theme (default) -->
<div class="faathum-widget faathum-theme-light">

<!-- Dark Theme -->
<div class="faathum-widget faathum-theme-dark">

<!-- Minimal Theme -->
<div class="faathum-widget faathum-theme-minimal">

<!-- Corporate Theme -->
<div class="faathum-widget faathum-theme-corporate">`}</code></pre>
              </div>

              <h3>Responsive Behavior</h3>
              <p>Control how widgets adapt to different screen sizes:</p>
              <div className="docs-code-block">
                <pre><code>{`@media (max-width: 768px) {
  .faathum-widget {
    --font-size-base: 12px;
    --padding-md: 12px;
  }
  
  .faathum-widget__dropdown {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
  }
}`}</code></pre>
              </div>

              <h3>Configuration Options</h3>
              <p>Programmatic customization through widget configuration:</p>
              <div className="docs-code-block">
                <pre><code>{`FaathumWidget.create({
  target: '#widget-container',
  config: {
    // Appearance
    theme: 'light',
    buttonStyle: 'primary',
    showLabels: true,
    compactMode: false,
    
    // Behavior
    autoOpen: false,
    closeOnSelect: true,
    enableKeyboard: true,
    
    // Analytics
    trackEvents: true,
    customEvents: {
      onOpen: (data) => console.log('Widget opened', data),
      onSelect: (data) => console.log('Option selected', data)
    }
  }
});`}</code></pre>
              </div>
            </div>
          )}

          {activeSection === 'analytics' && (
            <div className="docs-section">
              <h2>📊 Analytics & Tracking</h2>
              <p>Monitor widget usage and user behavior with comprehensive analytics tracking.</p>
              
              <h3>Event Tracking</h3>
              <p>Faathum automatically tracks key user interactions:</p>
              <div className="docs-field-list">
                <div className="docs-field">
                  <strong>explore_ai_open:</strong> Widget dropdown opened
                </div>
                <div className="docs-field">
                  <strong>explore_ai_mode_selected:</strong> Analysis mode chosen
                </div>
                <div className="docs-field">
                  <strong>explore_ai_audience_selected:</strong> Target audience selected
                </div>
                <div className="docs-field">
                  <strong>explore_ai_platform_selected:</strong> AI platform chosen
                </div>
                <div className="docs-field">
                  <strong>explore_ai_copy_prompt:</strong> Prompt copied to clipboard
                </div>
                <div className="docs-field">
                  <strong>explore_ai_launch:</strong> External AI platform launched
                </div>
              </div>

              <h3>Event Data Structure</h3>
              <p>Each event includes comprehensive context data:</p>
              <div className="docs-code-block">
                <pre><code>{`{
  event: "explore_ai_platform_selected",
  timestamp: "2026-04-05T17:30:00Z",
  sessionId: "sess_abc123",
  userId: "user_xyz789", // if available
  
  // Widget Context
  widgetId: "widget_federal_reserve_data",
  datasetId: "fed_reserve_elasticity",
  datasetTitle: "Reserve Demand Elasticity",
  
  // User Selections
  mode: "trends",
  audience: "policy",
  platform: "claude",
  
  // Page Context
  pageUrl: "https://example.com/economic-data",
  pageTitle: "Economic Indicators Dashboard",
  referrer: "https://google.com/search",
  
  // Technical Context
  userAgent: "Mozilla/5.0...",
  screenResolution: "1920x1080",
  viewportSize: "1200x800"
}`}</code></pre>
              </div>

              <h3>Custom Analytics Integration</h3>
              <p>Connect Faathum events to your analytics platform:</p>
              <div className="docs-code-block">
                <pre><code>{`// Google Analytics 4
FaathumWidget.create({
  config: {
    onAnalytics: (event, data) => {
      gtag('event', event, {
        custom_parameter_1: data.mode,
        custom_parameter_2: data.audience,
        custom_parameter_3: data.platform
      });
    }
  }
});

// Adobe Analytics
FaathumWidget.create({
  config: {
    onAnalytics: (event, data) => {
      s.linkTrackVars = 'eVar1,eVar2,eVar3';
      s.eVar1 = data.mode;
      s.eVar2 = data.audience;
      s.eVar3 = data.platform;
      s.tl(true, 'o', event);
    }
  }
});

// Custom Analytics
FaathumWidget.create({
  config: {
    onAnalytics: (event, data) => {
      fetch('/api/analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event, data })
      });
    }
  }
});`}</code></pre>
              </div>

              <h3>Privacy Compliance</h3>
              <p>Faathum respects user privacy and supports compliance requirements:</p>
              <ul>
                <li><strong>GDPR Compliance:</strong> No personal data collected without consent</li>
                <li><strong>Cookie-Free:</strong> Uses sessionStorage for temporary data only</li>
                <li><strong>Opt-Out Support:</strong> Disable tracking with <code>trackEvents: false</code></li>
                <li><strong>Data Minimization:</strong> Only essential interaction data is captured</li>
              </ul>

              <h3>Analytics Dashboard</h3>
              <p>Access usage insights through the Faathum dashboard:</p>
              <ul>
                <li>Widget engagement rates and popular datasets</li>
                <li>Analysis mode preferences by audience type</li>
                <li>AI platform selection trends</li>
                <li>Geographic usage patterns</li>
                <li>Performance metrics and error rates</li>
              </ul>
            </div>
          )}

          {activeSection === 'api-reference' && (
            <div className="docs-section">
              <h2>📚 API Reference</h2>
              <p>Complete technical reference for integrating and customizing Faathum widgets.</p>
              
              <h3>Widget Configuration API</h3>
              <div className="docs-code-block">
                <pre><code>{`interface ExploreConfig {
  // Required Fields
  id: string;                    // Unique widget identifier
  title: string;                 // Dataset display name
  description: string;           // Dataset description
  sourceName: string;           // Data source organization
  sourceUrl: string;            // Link to data source
  
  // Optional Fields
  format?: string;              // Data format (default: "Interactive Visualization")
  lastUpdated?: string;         // Last update timestamp
  context?: ExploreContext;     // Real-time page context
  audience?: Audience;          // Target audience (default: "general")
  
  // Analysis Configuration
  modes?: AnalysisMode[];       // Available analysis modes
  enabledPlatforms?: AIPlatform[]; // Enabled AI platforms
  
  // UI Configuration
  buttonStyle?: ButtonStyle;    // Button appearance
  theme?: Theme;               // Visual theme
  compactMode?: boolean;       // Compact layout mode
}`}</code></pre>
              </div>

              <h3>Widget Creation</h3>
              <div className="docs-code-block">
                <pre><code>{`// Basic Widget Creation
const widget = FaathumWidget.create({
  target: '#widget-container',
  config: {
    id: 'my-dataset',
    title: 'Economic Indicators',
    description: 'Federal Reserve economic data',
    sourceName: 'Federal Reserve',
    sourceUrl: 'https://fred.stlouisfed.org/'
  }
});

// Advanced Configuration
const widget = FaathumWidget.create({
  target: document.getElementById('widget'),
  config: {
    // ... basic config
    audience: 'researcher',
    modes: ['general', 'trends', 'anomalies'],
    enabledPlatforms: ['chatgpt', 'claude'],
    theme: 'dark',
    compactMode: true
  },
  
  // Event Handlers
  onAnalytics: (event, data) => {
    console.log('Analytics:', event, data);
  },
  
  onError: (error) => {
    console.error('Widget error:', error);
  }
});`}</code></pre>
              </div>

              <h3>Widget Methods</h3>
              <div className="docs-code-block">
                <pre><code>{`// Widget Instance Methods
widget.updateConfig(newConfig);     // Update configuration
widget.open();                      // Programmatically open dropdown
widget.close();                     // Close dropdown
widget.destroy();                   // Remove widget from DOM
widget.getConfig();                 // Get current configuration
widget.getAnalytics();             // Get usage analytics

// Static Methods
FaathumWidget.getVersion();        // Get library version
FaathumWidget.isSupported();       // Check browser compatibility
FaathumWidget.getAllWidgets();     // Get all widget instances`}</code></pre>
              </div>

              <h3>Context Injection API</h3>
              <div className="docs-code-block">
                <pre><code>{`interface ExploreContext {
  selectedSeries?: string[];      // Currently selected data series
  dateRange?: [string, string];   // Active date range filter
  filters?: Record<string, any>;  // Applied filters
  chartTitle?: string;           // Current chart title
  tableState?: string;           // Table sorting/filtering state
  articleContext?: string;       // Surrounding article content
  customData?: Record<string, any>; // Custom context data
}

// Dynamic Context Updates
widget.updateContext({
  selectedSeries: ['unemployment', 'inflation'],
  dateRange: ['2020-01-01', '2024-12-31'],
  filters: { region: 'US', sector: 'technology' }
});`}</code></pre>
              </div>

              <h3>Platform Adapter API</h3>
              <div className="docs-code-block">
                <pre><code>{`// Custom Platform Integration
FaathumWidget.registerPlatform('custom-ai', {
  name: 'Custom AI Platform',
  baseUrl: 'https://custom-ai.com/chat',
  supportsPromptPrefill: true,
  
  launch: (prompt, config) => {
    const url = \`https://custom-ai.com/chat?prompt=\${encodeURIComponent(prompt)}\`;
    window.open(url, '_blank');
  }
});

// Platform Configuration
interface PlatformConfig {
  name: string;
  baseUrl: string;
  supportsPromptPrefill: boolean;
  launch: (prompt: string, config: ExploreConfig) => void;
}`}</code></pre>
              </div>

              <h3>Error Handling</h3>
              <div className="docs-code-block">
                <pre><code>{`// Error Types
interface FaathumError {
  code: string;
  message: string;
  details?: any;
}

// Common Error Codes
'INVALID_CONFIG'     // Configuration validation failed
'MISSING_TARGET'     // Target element not found
'PLATFORM_ERROR'     // AI platform launch failed
'NETWORK_ERROR'      // Network request failed
'BROWSER_UNSUPPORTED' // Browser compatibility issue

// Error Handling
widget.onError = (error) => {
  switch (error.code) {
    case 'PLATFORM_ERROR':
      // Show fallback instructions
      break;
    case 'NETWORK_ERROR':
      // Retry or show offline message
      break;
    default:
      console.error('Faathum error:', error);
  }
};`}</code></pre>
              </div>
            </div>
          )}

          {activeSection === 'troubleshooting' && (
            <div className="docs-section">
              <h2>🔧 Troubleshooting</h2>
              <p>Common issues and solutions for Faathum widget implementation.</p>
              
              <h3>Widget Not Loading</h3>
              <div className="docs-troubleshoot-item">
                <h4>Problem:</h4>
                <p>Widget container appears empty or shows loading indefinitely.</p>
                <h4>Solutions:</h4>
                <ul>
                  <li>Verify the widget ID is correct</li>
                  <li>Check that the script is loaded before initialization</li>
                  <li>Ensure the target element exists in the DOM</li>
                  <li>Check browser console for JavaScript errors</li>
                </ul>
              </div>

              <h3>AI Platform Links Not Working</h3>
              <div className="docs-troubleshoot-item">
                <h4>Problem:</h4>
                <p>Clicking AI platform buttons doesn't open the expected platform.</p>
                <h4>Solutions:</h4>
                <ul>
                  <li>Check if popup blockers are preventing new windows</li>
                  <li>Verify platform URLs are accessible from user's location</li>
                  <li>Ensure the widget configuration includes enabled platforms</li>
                </ul>
              </div>

              <h3>Styling Issues</h3>
              <div className="docs-troubleshoot-item">
                <h4>Problem:</h4>
                <p>Widget appearance doesn't match your site design.</p>
                <h4>Solutions:</h4>
                <ul>
                  <li>Use CSS custom properties to override default styles</li>
                  <li>Check for CSS conflicts with existing site styles</li>
                  <li>Ensure proper CSS specificity for custom styles</li>
                </ul>
              </div>

              <h3>Getting Help</h3>
              <p>If you need additional support:</p>
              <ul>
                <li>Check our <a href="#api-reference">API Reference</a> for technical details</li>
                <li>Contact support at support@faathum.com</li>
                <li>Join our community forum for peer assistance</li>
              </ul>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default DocumentationPage;
